import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withKeys as _withKeys, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = ["aria-expanded", "aria-controls", "aria-activedescendant", "placeholder", "value", "disabled", "data-testid", "onKeydown"]
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = ["id"]
const _hoisted_6 = {
  key: 0,
  class: "px-3 pb-1 pt-2 text-[11px] font-bold uppercase text-gray-500"
}
const _hoisted_7 = ["id", "aria-selected", "disabled", "data-testid", "onClick"]
const _hoisted_8 = { class: "flex-1" }
const _hoisted_9 = {
  key: 0,
  class: "rounded-full bg-gray-100 px-2 text-[10px] text-gray-600"
}
const _hoisted_10 = {
  key: 1,
  class: "text-[11px] text-gray-400"
}
const _hoisted_11 = {
  key: 0,
  class: "px-3 py-2 text-sm text-gray-500"
}
const _hoisted_12 = ["data-testid"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'


export default /*#__PURE__*/_defineComponent({
  __name: 'CatalogCombobox',
  props: {
    label: {},
    displayValue: {},
    displayBadge: { default: null },
    emptyText: {},
    placeholder: { default: 'Buscar' },
    sections: {},
    search: {},
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    noResultsText: { default: 'Nada encontrado.' },
    footerNote: { default: null },
    testId: {}
  },
  emits: ["update:search", "select", "clear", "open"],
  setup(__props: any, { emit }) {

const props = __props;





const root = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
/** Só depois de digitar o input mostra a busca; até lá mostra o valor atual selecionado. */
const typed = ref(false)
/** mousedown dentro do componente (ex.: barra de rolagem da lista) antes de um focusout. */
const pointerDownInside = ref(false)
let selectOnMouseUp = false
const activeKey = ref<string | null>(null)
const inputId = `${props.testId}-${Math.random().toString(36).slice(2, 8)}`
const hasOptions = computed(() => props.sections.some((section) => section.options.length > 0))
const navigableOptions = computed(() =>
  props.sections.flatMap((section) => section.options).filter((option) => !option.disabled)
)
const activeOption = computed(
  () => navigableOptions.value.find((option) => option.key === activeKey.value) ?? null
)
const activeOptionId = computed(() =>
  isOpen.value && activeOption.value ? optionDomId(activeOption.value) : undefined
)

function optionDomId(option: CatalogOption): string {
  return `${inputId}-opt-${option.key}`
}

function isActive(option: CatalogOption): boolean {
  return isOpen.value && activeKey.value === option.key
}

function open(): void {
  if (props.disabled || isOpen.value) return
  isOpen.value = true
  typed.value = false
  activeKey.value = null
  selectOnMouseUp = true
  emit('update:search', '')
  emit('open')
  // Mantém o valor atual visível e selecionado: digitar substitui.
  void nextTick(() => inputEl.value?.select())
}

function close(): void {
  isOpen.value = false
  typed.value = false
  activeKey.value = null
}

/** No Chrome/Safari o mouseup do clique que deu foco desfaz a seleção feita no focus. */
function onMouseUp(event: MouseEvent): void {
  if (!selectOnMouseUp) return
  selectOnMouseUp = false
  event.preventDefault()
}

function onInput(event: Event): void {
  // Reabre ao digitar depois de escolher/Esc com o foco ainda no input.
  if (!isOpen.value) open()
  typed.value = true
  selectOnMouseUp = false
  emit('update:search', (event.target as HTMLInputElement).value)
}

function moveActive(step: 1 | -1): void {
  if (!isOpen.value) {
    open()
    return
  }
  const options = navigableOptions.value
  if (!options.length) return
  const current = options.findIndex((option) => option.key === activeKey.value)
  const next =
    current === -1
      ? step === 1
        ? 0
        : options.length - 1
      : (current + step + options.length) % options.length
  activeKey.value = options[next].key
  void nextTick(() => {
    const el = document.getElementById(optionDomId(options[next]))
    el?.scrollIntoView?.({ block: 'nearest' })
  })
}

function onEnter(event: KeyboardEvent): void {
  if (!isOpen.value || !activeOption.value) return
  event.preventDefault()
  choose(activeOption.value)
}

watch(
  () => props.search,
  () => (activeKey.value = null)
)

/**
 * Fecha ao sair do componente por teclado (Tab). Cliques internos mantêm o foco; um
 * focusout sem relatedTarget logo após mousedown interno (barra de rolagem) também não fecha.
 */
function onFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget as Node | null
  const pointerInside = pointerDownInside.value
  pointerDownInside.value = false
  if (next && root.value?.contains(next)) return
  if (!next && pointerInside) return
  close()
}

function choose(option: CatalogOption): void {
  if (option.disabled) return
  emit('select', option)
  close()
}

function optionClass(option: CatalogOption): string {
  if (option.kind === 'action') return 'font-semibold text-primary30'
  if (option.kind === 'clear') return 'italic text-gray-500'
  return 'text-gray-800'
}

function onDocumentMouseDown(event: MouseEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) close()
}

function onDocumentMouseUp(): void {
  pointerDownInside.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentMouseDown)
  document.addEventListener('mouseup', onDocumentMouseUp)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown)
  document.removeEventListener('mouseup', onDocumentMouseUp)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "relative w-full text-left",
    "data-testid": _ctx.testId,
    onMousedown: _cache[4] || (_cache[4] = ($event: any) => (pointerDownInside.value = true)),
    onFocusout: onFocusOut
  }, [
    _createElementVNode("label", {
      class: "text-xs font-bold text-gray-600",
      for: inputId
    }, _toDisplayString(_ctx.label), 1),
    _createElementVNode("div", {
      class: _normalizeClass(["mt-1 flex h-[44px] items-center gap-2 rounded border px-3", 
        _ctx.disabled
          ? 'cursor-not-allowed bg-gray-50 border-gray-200'
          : 'bg-white border-gray-300 focus-within:border-primary20'
      ])
    }, [
      _createElementVNode("input", {
        id: inputId,
        ref_key: "inputEl",
        ref: inputEl,
        role: "combobox",
        autocomplete: "off",
        class: _normalizeClass(["min-w-0 flex-1 bg-transparent text-sm outline-none", !isOpen.value && !_ctx.displayValue ? 'italic text-gray-400' : 'text-gray-900']),
        "aria-expanded": isOpen.value,
        "aria-controls": `${inputId}-list`,
        "aria-activedescendant": activeOptionId.value,
        placeholder: isOpen.value ? _ctx.placeholder : _ctx.displayValue || _ctx.emptyText,
        value: isOpen.value && typed.value ? _ctx.search : _ctx.displayValue,
        disabled: _ctx.disabled,
        "data-testid": `${_ctx.testId}-input`,
        onFocus: open,
        onClick: open,
        onMouseup: onMouseUp,
        onInput: onInput,
        onKeydown: [
          _withKeys(close, ["esc"]),
          _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (moveActive(1)), ["prevent"]), ["down"])),
          _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (moveActive(-1)), ["prevent"]), ["up"])),
          _withKeys(onEnter, ["enter"])
        ]
      }, null, 42, _hoisted_2),
      (!isOpen.value && _ctx.displayBadge)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "rounded-full bg-primary5 px-2 text-[10px] font-semibold text-primary30",
            "data-testid": `${_ctx.testId}-badge`
          }, _toDisplayString(_ctx.displayBadge), 9, _hoisted_3))
        : _createCommentVNode("", true),
      (!isOpen.value && _ctx.clearable && _ctx.displayValue && !_ctx.disabled)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            class: "text-gray-400 hover:text-gray-700",
            "aria-label": "Limpar",
            "data-testid": `${_ctx.testId}-clear-button`,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('clear')))
          }, " ✕ ", 8, _hoisted_4))
        : _createCommentVNode("", true)
    ], 2),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          id: `${inputId}-list`,
          role: "listbox",
          class: "absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.sections, (section) => {
            return (_openBlock(), _createElementBlock(_Fragment, {
              key: section.key
            }, [
              (section.title)
                ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(section.title), 1))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.options, (option) => {
                return (_openBlock(), _createElementBlock("button", {
                  id: optionDomId(option),
                  key: option.key,
                  type: "button",
                  role: "option",
                  class: _normalizeClass(["flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400", [optionClass(option), { 'bg-gray-100': isActive(option) }]]),
                  "aria-selected": isActive(option),
                  disabled: option.disabled,
                  "data-testid": option.testId,
                  onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
                  onClick: ($event: any) => (choose(option))
                }, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(option.label), 1),
                  (option.badge)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(option.badge), 1))
                    : _createCommentVNode("", true),
                  (option.hint)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(option.hint), 1))
                    : _createCommentVNode("", true)
                ], 42, _hoisted_7))
              }), 128))
            ], 64))
          }), 128)),
          (!hasOptions.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.noResultsText), 1))
            : _createCommentVNode("", true),
          (_ctx.footerNote)
            ? (_openBlock(), _createElementBlock("p", {
                key: 1,
                class: "border-t border-gray-100 px-3 py-2 text-xs text-gray-500",
                "data-testid": `${_ctx.testId}-footer-note`
              }, _toDisplayString(_ctx.footerNote), 9, _hoisted_12))
            : _createCommentVNode("", true)
        ], 8, _hoisted_5))
      : _createCommentVNode("", true)
  ], 40, _hoisted_1))
}
}

})